import {
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  TableContainer,
  Spinner,
  Center,
  Box,
  Th,
  Flex,
} from "@chakra-ui/react";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import PageHeader from "components/layout/PageHeader";
import { AlertsContext } from "providers/alerts";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "utils/api";
import AddPropertyButton from "./AddPropertyButton";
import { formatAddress } from "utils/helpers";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { hasPermission } from "utils/permissionHelper";
import { ErrorBoundary } from "@sentry/react";
import { getErrorMessage } from "utils/errorHandler";
import { Pagination } from "components/elements/Pagination/pagination";
import InputSearch from "components/elements/InputSearch/input-search";

interface Property {
  id: number;
  name: string;
  address: string;
  borrower_company_id: string;
  borrower_company_name: string;
  loans_count: number;
}

function Properties() {
  const { user } = useTenant();
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<Property[]>([]);
  const { showError } = useContext(AlertsContext);

  const isReadPropertyAllowed = hasPermission(user, "read_property");
  const isReadAllPropertyOnlyAllowed = hasPermission(user, "read_all_property_only");
  const [filteredProperty, setFilteredProperty] = useState<Property[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  const totalPages = Math.ceil(filteredProperty.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleProperties = filteredProperty.slice(startIndex, startIndex + PAGE_SIZE);

  async function onLoad() {
    try {
      setLoading(true);
      const response = await apiRequest<{ properties: Property[] }>("/supabase/properties/select");
      setProperties(response.properties);
      setFilteredProperty(response.properties);
    } catch (err) {
      showError({
        title: "Properties Not Loaded",
        description: getErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    onLoad();
  }, []);

  useEffect(() => {
    const filtered = properties.filter(property =>
      `${property.name}`.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProperty(filtered);
    setCurrentPage(1);
  }, [searchQuery, properties]);

  return (
    <>
      <PageHeader title="Properties" />
      {loading ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <WidgetBox>
          <Flex flexDirection="column" gap={4}>
            {!isReadAllPropertyOnlyAllowed &&
              !isReadPropertyAllowed &&
              user.type !== "borrower" && (
                <Box display="flex" justifyContent={"space-between"}>
                  <Box w={"300px"}>
                    <InputSearch
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                  </Box>
                  <AddPropertyButton onSuccess={onLoad} />
                </Box>
              )}

            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Property Name</Th>
                    <Th>Property Address</Th>
                    <Th>Borrower Company</Th>
                    <Th>Total Loans</Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {visibleProperties.map(p => (
                    <Tr key={p.id}>
                      <Td>
                        <Link to={`/properties/${p.id}`}>{p.name}</Link>
                      </Td>
                      <Td>{formatAddress(p.address)}</Td>
                      <Td>
                        <Link to={`/companies/${p.borrower_company_id}`}>
                          {p.borrower_company_name}
                        </Link>
                      </Td>
                      <Td>{p.loans_count}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>

            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
              totalCount={properties.length}
              pageSize={PAGE_SIZE}
            />
          </Flex>
        </WidgetBox>
      )}
    </>
  );
}

function PropertiesFallback() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "1rem",
          backgroundColor: "white",
          borderRadius: "1rem",
          padding: "2rem",
        }}
      >
        <h1
          style={{
            fontSize: "1.5rem",
          }}
        >
          There was an error loading properties.
        </h1>
        <p>We're investigating the issue.</p>
      </div>
    </div>
  );
}
const BoundedProperties = () => (
  <ErrorBoundary fallback={<PropertiesFallback />}>
    <Properties />
  </ErrorBoundary>
);
export default BoundedProperties;
